/**
 * Main Application Component
 *
 * Sets up routing, providers, and authentication.
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { AuthProvider, useAuth, LoginPage, RoleGuard } from './features/auth';
import { RoleNotAssignedNotice } from './features/auth/RoleNotAssignedNotice';
import { EmployeesPage, EmployeeDetailPage, EmployeeProfilePrintPage, MyTasksPage } from './features/employees';
import { MijnVerlofPage, ApprovalQueuePage, TeamVerlofPage } from './features/verlof';
import { MijnRoosterPage } from './features/werkrooster/MijnRoosterPage';
import { AanwezigheidPage } from './features/aanwezigheid';
import { TakenBeheerPage, KwalificatieMatrixPage, PlanbordPage, BezettingPage, MijnIndelingPage } from './features/planning';
import { TasksPage } from './features/tasks';
import { CompetenciesPage } from './features/competencies';
import { NiveausPage } from './features/niveaus';
import { DataImportPage } from './features/dataImport';
import { SelfEvaluationPage, EvaluationWizard, ConversationModePage, ScoringFormPrintPage, AssessmentReportPrintPage, SavedAssessmentReportPage } from './features/evaluations';
import { PeerEvaluationDetailPage, PeerEvaluationPrintPage } from './features/peer-evaluations';
import { PublicPeerEvaluationPage } from './features/public';
import { TeamDashboardPage, TeamPeerEvaluationsPage, TeamEvaluationsPage } from './features/team';
import { HRDashboardPage, HRReviewPage, HRComparisonPage, VerlofAfsluitenPage } from './features/hr';
import { EvaluationHistoryPage, TaskExplorerPage, GrowthPathPage, CycleProgressPage } from './features/dashboard';
import { ManagerNotesPage, MeetingNotesPage } from './features/notes';
import { PopPage } from './features/pop';
import { AgreementsPage } from './features/agreements';
import { ManualPage } from './features/manual';
import { ManualAdminPage } from './features/manual-admin';
import { ExportPage, PdfPreviewPage } from './features/export';
import { SettingsPage, CompetencyMatrixPrintPage } from './features/settings';
import { DashboardPage } from './pages/DashboardPage';
import { ToastProvider } from './components/Toast';
import { useSSEConnection, useRealTimeEvaluations, useSSENotifications } from './hooks/useRealTimeUpdates';
import { ConnectionStatus } from './components/ConnectionStatus';
import { useEffect, useRef } from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * Clears query cache when user logs out to prevent data leaks
 */
function CacheClearer() {
  const { isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const wasAuthenticated = useRef(isAuthenticated);

  useEffect(() => {
    // Clear cache when transitioning from authenticated to unauthenticated
    if (wasAuthenticated.current && !isAuthenticated) {
      qc.clear();
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, qc]);

  return null;
}

/**
 * SSE connection manager component
 */
function RealTimeManager() {
  useSSEConnection();
  useRealTimeEvaluations();
  useSSENotifications();
  return <ConnectionStatus />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-spinner">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <RoleNotAssignedNotice />
          <CacheClearer />
          <RealTimeManager />
          <BrowserRouter>
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/evaluatie/:token" element={<PublicPeerEvaluationPage />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />

            {/* HR/Admin routes */}
            <Route
              path="/medewerkers"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <EmployeesPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medewerkers/:id"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <EmployeeDetailPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medewerkers/:id/print"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <EmployeeProfilePrintPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Tasks routes */}
            <Route
              path="/taken"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <TasksPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Competencies routes */}
            <Route
              path="/competenties"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <CompetenciesPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Niveaus routes */}
            <Route
              path="/niveaus"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <NiveausPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Employee routes */}
            <Route
              path="/mijn-taken"
              element={
                <ProtectedRoute>
                  <MyTasksPage />
                </ProtectedRoute>
              }
            />
            {/* Spoor 2 (Verlof MVP) — Story 1.2 */}
            <Route
              path="/mijn-verlof"
              element={
                <ProtectedRoute>
                  <MijnVerlofPage />
                </ProtectedRoute>
              }
            />
            {/* Spoor 2 (Verlof MVP) — Story 3.1. Nog niet in de navigatie
                opgenomen — dat gebeurt in Epic 5 (Story 5.2, rolgebaseerde
                navigatie), zelfde bewuste volgorde als Story 1.2/1.4. */}
            <Route
              path="/goedkeuringen"
              element={
                <ProtectedRoute>
                  <ApprovalQueuePage />
                </ProtectedRoute>
              }
            />
            {/* Story 8.4 (besluit V39): de leesweg van de leidinggevende naar
                het verloop van een verlofaanvraag van zijn teamlid. Bewust een
                eigen pagina naast /goedkeuringen: daar staat wat hij moet
                beslissen, hier wat hij mag lezen (FR-24, besluit V27). */}
            <Route
              path="/team/verlof"
              element={
                <ProtectedRoute>
                  <TeamVerlofPage />
                </ProtectedRoute>
              }
            />
            {/* Spoor 2 (Verlof MVP) — Story 1.4 */}
            <Route
              path="/mijn-rooster"
              element={
                <ProtectedRoute>
                  <MijnRoosterPage />
                </ProtectedRoute>
              }
            />
            {/* Spoor 2 (Verlof MVP) — Story 4.2. Nog niet in de navigatie
                opgenomen (Epic 5, Story 5.2 verzorgt de nav-integratie) —
                route bestaat al zodat de pagina direct testbaar/bereikbaar
                is, conform het incrementele opleverpatroon van dit project. */}
            <Route
              path="/aanwezigheid"
              element={
                <ProtectedRoute>
                  <AanwezigheidPage />
                </ProtectedRoute>
              }
            />
            {/* Epic C-1 (Capaciteitsplanning), Story C-1.3. Nog niet in de
                navigatie opgenomen (Story C-5.2 verzorgt de nav-integratie) —
                route bestaat al zodat de pagina direct testbaar/bereikbaar
                is, zelfde incrementele opleverpatroon als /aanwezigheid
                hierboven. Guard = HR/admin OF isTeamManager (capability, geen
                statische rol) — server-side afgedwongen door
                assertPlanningAccess (Story C-1.2); bewust geen RoleGuard
                hier, zelfde patroon als /goedkeuringen (ApprovalQueuePage). */}
            <Route
              path="/planning/taken"
              element={
                <ProtectedRoute>
                  <TakenBeheerPage />
                </ProtectedRoute>
              }
            />
            {/* Epic C-2 (Kwalificaties), Story C-2.2. Zelfde incrementele
                opleverpatroon als /planning/taken hierboven — nog niet in de
                navigatie (Story C-5.2), guard server-side (planner-lees +
                assertTeamManagerOrHR/requireRole per sub-actie). */}
            <Route
              path="/planning/kwalificaties"
              element={
                <ProtectedRoute>
                  <KwalificatieMatrixPage />
                </ProtectedRoute>
              }
            />
            {/* Epic C-3 (Planbord & indelen), Story C-3.3. Zelfde incrementele
                opleverpatroon als /planning/taken/kwalificaties hierboven —
                nog niet in de navigatie (Story C-5.2), guard server-side
                (planningService.getBoard's resolveBoardScope, 403
                NOT_TEAM_MANAGER voor wie geen team-manager-capability of
                HR/admin heeft). */}
            <Route
              path="/planning/planbord"
              element={
                <ProtectedRoute>
                  <PlanbordPage />
                </ProtectedRoute>
              }
            />
            {/* Epic C-4 (Bezetting & conflicten), Story C-4.1. Zelfde
                incrementele opleverpatroon als /planning/planbord hierboven —
                nog niet in de navigatie (Story C-5.2), guard server-side
                (planningService.getOccupancy hergebruikt getBoard's
                resolveBoardScope). Ook bereikbaar via de
                bezettingsstrook-doorklik op het planbord (AC 4). */}
            <Route
              path="/planning/bezetting"
              element={
                <ProtectedRoute>
                  <BezettingPage />
                </ProtectedRoute>
              }
            />
            {/* Epic C-5 (Mijn indeling & navigatie), Story C-5.1. Nog niet in de
                navigatie opgenomen (Story C-5.2 verzorgt de nav-integratie) —
                route bestaat al zodat de pagina + het dashboard-strook-doorklik
                (EigenIndelingWidget) direct werken, zelfde incrementele
                opleverpatroon als /planning/* hierboven. Guard = elke
                geauthenticeerde gebruiker (server-side strikt eigen-scope via
                planningService.getMyAssignments, geen RoleGuard nodig). */}
            <Route
              path="/mijn-indeling"
              element={
                <ProtectedRoute>
                  <MijnIndelingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mijn-beoordeling/:id"
              element={
                <ProtectedRoute>
                  <SelfEvaluationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mijn-historie"
              element={
                <ProtectedRoute>
                  <EvaluationHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/taken-verkennen"
              element={
                <ProtectedRoute>
                  <TaskExplorerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mijn-groeipad"
              element={
                <ProtectedRoute>
                  <GrowthPathPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mijn-pop"
              element={
                <ProtectedRoute>
                  <PopPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pop/:employeeId"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <PopPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ontwikkelingsplan"
              element={
                <ProtectedRoute>
                  <PopPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/afspraken"
              element={
                <ProtectedRoute>
                  <AgreementsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/peer-evaluatie/:id"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <PeerEvaluationDetailPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/peer-evaluatie/:id/print"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <PeerEvaluationPrintPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Manager routes */}
            <Route
              path="/team"
              element={
                <ProtectedRoute>
                  {/* O3 (2026-07-29): ook toegankelijk voor wie daadwerkelijk
                      een afdeling aanstuurt maar niet de rol `beoordelaar`
                      heeft. Verruimend — de drie rollen blijven gelden. */}
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']} allowTeamManager>
                    <TeamDashboardPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/peer-feedback"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <TeamPeerEvaluationsPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/beoordelingen"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <TeamEvaluationsPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/beoordeling/:id/wizard"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <EvaluationWizard />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/beoordeling/:id/gesprek"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <ConversationModePage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/beoordeling/:id/pdf-preview"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <PdfPreviewPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/beoordeling/:id/print"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <ScoringFormPrintPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/beoordeling/:id/rapport"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <AssessmentReportPrintPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/beoordelingsverslag/:id"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <SavedAssessmentReportPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notities"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <ManagerNotesPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gespreksverslagen"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['beoordelaar', 'hr', 'admin']}>
                    <MeetingNotesPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* HR routes */}
            <Route
              path="/hr"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <HRDashboardPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/beoordeling/:id"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <HRReviewPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/vergelijken"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <HRComparisonPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            {/* Story 4.2 (Verlof correctie): bewust eerst als route, nog geen
                navigatie-item in Layout.tsx — hetzelfde incrementele
                opleverpatroon als /aanwezigheid en /planning/taken. */}
            <Route
              path="/hr/verlof-afsluiten"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <VerlofAfsluitenPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/cyclus-voortgang"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <CycleProgressPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Manual/Handbook routes */}
            <Route
              path="/handleiding"
              element={
                <ProtectedRoute>
                  <ManualPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/handleiding/:slug"
              element={
                <ProtectedRoute>
                  <ManualPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/handleiding"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <ManualAdminPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Export routes */}
            <Route
              path="/export"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <ExportPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/data-import"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles="admin">
                    <DataImportPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/instellingen"
              element={
                <ProtectedRoute>
                  {/* Story TD-2 (tech-debt-followup): verzacht van "admin" naar
                      ['admin', 'hr'] -- veel Instellingen-tabs staan hr al toe
                      op backend-niveau (zie SettingsPage.tsx's per-tab
                      `roles`-filtering voor de tabs die wél strikt admin-only
                      blijven, bv. Migratie kerntaken en Rollen & Rechten). */}
                  <RoleGuard allowedRoles={['admin', 'hr']}>
                    <SettingsPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/competency-matrix/print"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['hr', 'admin']}>
                    <CompetencyMatrixPrintPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Redirect root to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* 404 */}
            <Route
              path="*"
              element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-900">404</h1>
                    <p className="mt-2 text-gray-600">Pagina niet gevonden</p>
                  </div>
                </div>
              }
            />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

